import { z } from 'zod';

/**
 * Card-back private state — the two per-viewer surfaces of the card back that
 * have had storage on disk since `0003_card_detail.sql` and no way to reach it.
 *
 * `FE-14.2` and `FE-16.1`; lead requirement `FR-CARD-003`.
 *
 * Two tables, one theme:
 *
 *   - `card_description_drafts` (`card_id`, `user_id`, `draft_text`,
 *     `updated_at`) — `FR-CARD-003` AC2, "given the user has typed unsaved text
 *     into the description, when they close the card back and reopen the same
 *     card, then the unsaved text is still present in the editor". The shipped
 *     browser satisfies that from `localStorage`, which keeps the promise on
 *     ONE browser; the table is keyed `(card_id, user_id)` and so has been
 *     account-scoped from the day it was written. What was missing is a route,
 *     and therefore a contract.
 *   - `user_preferences.activity_section_collapsed` — `FR-CARD-034`'s
 *     Behaviour, "the preference persists per user across cards", and AC2.
 *     Same shape of gap: a column, a browser-local twin, and nothing on the
 *     wire between them.
 *
 * ⚠ **THE SHAPES BELOW ARE CITED, NOT DESIGNED.** B14 settled this contract
 * when it deliberately declined to build it, and three docblocks carry the
 * ruling. `./card-detail`'s header: "a block that wants the account-scoped
 * version adds `PUT`/`DELETE /cards/:id/description/draft` with a shape of its
 * own — it is not a nullable field on the patch body below, because a draft and
 * a save are different intentions and a body that carried both would let a
 * client save by accident." The path, the methods and the separate shape are
 * that sentence; `card-detail.test.ts`'s "refuses a draft key" and its
 * `descriptionDraft`-absent assertion over `cardDetailSchema.shape` are the
 * same ruling enforced, and **nothing in this file makes either of them false**.
 * Not one shipped schema is widened to reach this state.
 *
 * ⚠ **NOTHING HERE EMITS AN EVENT, AND THAT IS THE CONTRACT RATHER THAN AN
 * OMISSION.** `./events` says so in terms — "there is no draft event at all …
 * a keystroke-rate event on an append-only table is a different animal from an
 * explicit Save" — and `events.test.ts` asserts `card.description_drafted` is
 * absent from `EVENT_TYPE_NAMES`. Both surfaces here are private per-viewer
 * state on a shared subject: a draft is one member's half-typed sentence and a
 * collapsed section is one member's window, neither is visible to another
 * account, and so neither is a change to the shared state the event stream
 * records. A broadcast of either would put one member's typing in every other
 * member's activity feed at keystroke rate.
 *
 * The file follows the package's input/view division. The **views**
 * (`cardDescriptionDraftSchema`, `cardBackPreferencesSchema`) are plain
 * `z.object`s that render what the database holds. The **inputs**
 * (`cardDescriptionDraftPutSchema`,
 * `cardBackActivitySectionCollapsedPatchSchema`) are field-scoped
 * `z.strictObject`s, on SC-4's one-route-per-field rule and
 * `./accessibility-preferences`' precedent: an unrelated preference key must be
 * a validation failure rather than a silently stripped field, because three
 * different routes write three different columns of `user_preferences` and each
 * must be unable to name another's.
 */

// ---------------------------------------------------------------------------
// The cap — a SECOND declaration of one product rule against a SECOND column,
// and emphatically not a re-export of `CARD_DESCRIPTION_MAX_LENGTH`.
// ---------------------------------------------------------------------------

/**
 * The draft cap — 16,384 characters, `FR-CARD-003`'s own number.
 *
 * ⚠ **DECLARED HERE RATHER THAN IMPORTED FROM `./card-detail`, AND THAT FILE
 * NAMES THIS EXACT COLUMN AS THE CASE ITS CONSTANT MUST NOT COVER.**
 * `CARD_DESCRIPTION_MAX_LENGTH`'s docblock states the package's rule — "import
 * the constant that describes YOUR column if one exists, declare it if none
 * does, and never share one across two columns" — and then names the
 * exceptions: "it must not be shared with `card_description_drafts.draft_text`,
 * `boards.description` or `comments.body`, each of which is a separate column a
 * separate requirement could cap separately."
 *
 * The two numbers are equal today for a product reason, not a coincidence:
 * `FR-CARD-003`'s single cap governs the saved text and the unsaved text of the
 * same field, and a draft you could type but never save would be a trap. They
 * are two declarations of one rule against two columns, and the day one moves
 * the other need not.
 *
 * ⚠ **The route is the only enforcement there is.** `draft_text` is Postgres
 * `text` — unbounded, no CHECK — exactly as `cards.description` is, and
 * `0003_card_detail.sql` records that it is `text` for the same reason. Nothing
 * in the database refuses an over-cap draft; a `curl` walks straight past the
 * browser's counter. Counted in UTF-16 code units, because that is what
 * `z.string().max()` measures and what a browser's `textarea.value.length`
 * reports, so the server and the editor's character counter agree without
 * either converting.
 */
export const CARD_DESCRIPTION_DRAFT_MAX_LENGTH = 16384;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

/**
 * `GET /cards/:id/description/draft` — the caller's own unsaved description
 * text for one card, or the absence of one.
 *
 * ⚠ **`null` means NO ROW, and there is no other way to spell it.**
 * `card_description_drafts.draft_text` is `text NOT NULL`, so "this user has no
 * draft on this card" cannot be a row holding `NULL`; it is the absence of the
 * row. That is what makes clearing a draft a `DELETE` rather than a `PUT` of
 * `''` (see `cardDescriptionDraftPutSchema`), and it is why the two fields are
 * nullable together — a response carries either both values or neither.
 *
 * The route answers **200 with both fields `null`**, never 404, for a card the
 * caller can read but has never drafted on. A 404 here would be indistinguishable
 * from the 404 the guard answers for a card the caller cannot read at all, and
 * "no draft yet" is the ordinary case on every card back ever opened.
 */
export const cardDescriptionDraftSchema = z.object({
  /**
   * `card_description_drafts.draft_text text NOT NULL` — the caller's unsaved
   * text. `null` when there is no row.
   *
   * ⚠ **UNCAPPED ON THE WAY OUT, THOUGH THE PUT BODY CAPS IT ON THE WAY IN.**
   * This is `cardDetailSchema.description`'s input/view split applied to the
   * neighbouring column, and it is the single most likely field here to be
   * "fixed" for symmetry. The column is unbounded `text` with no CHECK, so a
   * longer value is storable by a fixture, a seed or any row written before
   * this route existed. A view that enforced the product cap would make
   * `GET /cards/:id/description/draft` 500 on a row the database legitimately
   * holds — and only for the users whose own typing provoked it. Reject on the
   * way in, render what is there on the way out.
   *
   * Not bounded below either: `''` is not a value this route writes (the PUT
   * body is `.min(1)`), but a row already holding it must render rather than
   * 500.
   */
  draftText: z.string().nullable(),
  /**
   * `card_description_drafts.updated_at timestamptz(3) NOT NULL DEFAULT now()`,
   * maintained by `trg_card_description_drafts_set_updated_at` with a
   * `WHEN (old.* IS DISTINCT FROM new.*)` clause — so re-`PUT`ing an identical
   * draft leaves it untouched, which is the property
   * `board-view-preferences.ts` documents for its own upsert. `null` when there
   * is no row.
   *
   * ⚠ **A bounded-below string and emphatically NOT `z.iso.datetime()`** — the
   * house rule for every `timestamptz` read back through `pg` in this package
   * (`cardDetailSchema.startAt`, `commentSchema.editedAt`, and every other
   * timestamp view). `pg` hands a `timestamptz` back in Postgres' own spelling
   * (`2026-08-12 12:00:00.123+00`: a space rather than a `T`, no trailing `Z`),
   * which is a legitimate instant and not an ISO 8601 form. `z.iso.datetime()`
   * here would make this the only view in the package to reject the value the
   * database actually produced.
   *
   * On the wire so a later chunk can compare two browsers' drafts. The web half
   * of this chunk deliberately does not: it reconciles local-before-server
   * because `localStorage` is written synchronously on every keystroke while
   * the mirror write is debounced, so on any one browser the local copy is
   * never the older of the two.
   */
  updatedAt: z.string().min(1).nullable(),
});

export type CardDescriptionDraft = z.infer<typeof cardDescriptionDraftSchema>;

/**
 * `GET /me/card-back-preferences` — the caller's own private card-back
 * settings.
 *
 * Its own contract and its own route file rather than a field on
 * `accessibilityPreferencesSchema`, `appearancePreferencesSchema` or
 * `notificationPreferencesSchema`. Those are three shipped schemas serving
 * three shipped route files over three disjoint sets of `user_preferences`
 * columns, and keeping a fourth set in a fourth file is what lets four
 * concurrent panels write the same row without any of them being able to name
 * another's column.
 *
 * A caller with no `user_preferences` row gets the column's own default rather
 * than a 404 — see the field.
 */
export const cardBackPreferencesSchema = z.object({
  /**
   * `user_preferences.activity_section_collapsed boolean NOT NULL DEFAULT
   * false` — `FR-CARD-034`: "the preference persists per user across cards",
   * and AC2, "given the section is expanded, when the user collapses it and
   * then opens a different card, then that card's section is also collapsed."
   *
   * `false` is "expanded", and it is the value a caller with **no
   * `user_preferences` row** receives: `requireUser` proves a session, not a
   * preferences row, so the route synthesises the column's own `DEFAULT`. That
   * synthesised value is not a new state — it is the same one the shipped
   * browser already shows for a missing `localStorage` key, so a client that
   * starts reading this route sees no change on a fresh account.
   *
   * ⚠ **`hide_activity_details` is the column next to it and is deliberately
   * NOT here.** That is `FR-COLLAB-019`'s, `notification-preferences.ts`
   * already assigns it elsewhere, and it has a browser-local twin of its own.
   * A second boolean on this shape would quietly claim it.
   */
  activitySectionCollapsed: z.boolean(),
});

export type CardBackPreferences = z.infer<typeof cardBackPreferencesSchema>;

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * `PUT /cards/:id/description/draft` — store the caller's unsaved description
 * text for one card.
 *
 * ⚠ **`.min(1)` IS LOAD-BEARING AND MUST NOT BE RELAXED TO ACCEPT `''`.**
 * `draft_text` is `NOT NULL` and "no draft" is "no row", so there is exactly
 * one spelling of "I have no unsaved text": `DELETE`. Two spellings of one
 * request is what `./card-detail` warns against when it refuses a draft key on
 * the description patch body.
 *
 * The consequence a caller must implement, and the reason this sentence is in
 * the contract rather than only in the client: a debounced writer sees the
 * keystroke that EMPTIES the field (select-all, delete) like any other, and a
 * writer that always `PUT`s would send `draftText: ''`, take a 422, and — if it
 * treats a failed background mirror as silent, which it should — leave the
 * server holding the PREVIOUS non-empty draft. The user would then open a
 * second browser and be handed back text they deliberately deleted. So an empty
 * debounced value is a `DELETE`, never a `PUT`.
 *
 * A `PUT` rather than a `PATCH` because the body is the whole draft, not a
 * change to one: there is one field, the client always holds all of it, and the
 * write is an idempotent upsert of the caller's own row.
 *
 * Over-cap is **422 `validation_failed`**, never a silent truncation — the same
 * answer `PATCH /cards/:id/description` gives for the same overflow. Truncating
 * would return a 200 over text the client believes it stored, which on a
 * surface whose entire promise is "your typing is safe" is the worst available
 * outcome.
 */
export const cardDescriptionDraftPutSchema = z.strictObject({
  draftText: z.string().min(1).max(CARD_DESCRIPTION_DRAFT_MAX_LENGTH),
});

export type CardDescriptionDraftPut = z.infer<typeof cardDescriptionDraftPutSchema>;

/**
 * `PATCH /me/card-back-preferences/activity-section-collapsed` — SC-4, one
 * route per field.
 *
 * Strict, so `hide_activity_details`, a theme, a language or any other
 * `user_preferences` key arriving in this body is a 422 rather than a field
 * this route silently ignores. The route's own `ON CONFLICT DO UPDATE` names
 * exactly one column for the same reason: the other twenty-two columns of the
 * row must be unreachable from here.
 */
export const cardBackActivitySectionCollapsedPatchSchema = z.strictObject({
  activitySectionCollapsed: z.boolean(),
});

export type CardBackActivitySectionCollapsedPatch = z.infer<
  typeof cardBackActivitySectionCollapsedPatchSchema
>;
